import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  getCurrentUser,
  fetchAuthSession
} from 'aws-amplify/auth';
import {
  S3Client,
  PutObjectCommand
} from '@aws-sdk/client-s3';
import {
  TranscribeClient,
  StartTranscriptionJobCommand,
  GetTranscriptionJobCommand
} from '@aws-sdk/client-transcribe';
import {
  LambdaClient,
  InvokeCommand
} from '@aws-sdk/client-lambda';

import { useMyContext } from '../MyContext';

const REGION = 'us-east-1';
const BUCKET_NAME = 'my-transcribe-audio-uploads';

const TranscribeTab = () => {
  const [file, setFile] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  const [transcript, setTranscript] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [lambdaResponse, setLambdaResponse] = useState('');
  const [lambdaLoading, setLambdaLoading] = useState(false);

  const {
    promptMaster,
    selectedClientCode,
    fetchPromptMaster
  } = useMyContext();

  useEffect(() => {
    setSelectedPrompts([]);
    fetchPromptMaster(true);
  }, []);

  const getAmplifyCredentials = async () => {
    await getCurrentUser();
    const session = await fetchAuthSession();
    return session.credentials;
  };

  const uploadToS3 = async (credentials) => {
    const s3Client = new S3Client({ region: REGION, credentials });
    const key = `uploads/${uuidv4()}-${file.name}`;
    const arrayBuffer = await file.arrayBuffer();
    const body = new Uint8Array(arrayBuffer);

    await s3Client.send(
      new PutObjectCommand({
        Bucket: BUCKET_NAME,
        Key: key,
        Body: body,
        ContentType: file.type || 'audio/wav',
      })
    );
    return key;
  };

  const startTranscription = async (credentials, s3Key) => {
    const transcribeClient = new TranscribeClient({ region: REGION, credentials });
    const jobName = `job-${uuidv4()}`;
    const mediaUri = `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/${s3Key}`;

    await transcribeClient.send(
      new StartTranscriptionJobCommand({
        TranscriptionJobName: jobName,
        LanguageCode: 'en-US',
        Media: { MediaFileUri: mediaUri },
        OutputBucketName: BUCKET_NAME,
      })
    );

    return { transcribeClient, jobName };
  };

  const pollTranscript = async (transcribeClient, jobName) => {
    let status = 'IN_PROGRESS';
    while (status === 'IN_PROGRESS') {
      await new Promise((res) => setTimeout(res, 5000));
      const { TranscriptionJob } = await transcribeClient.send(
        new GetTranscriptionJobCommand({ TranscriptionJobName: jobName })
      );
      status = TranscriptionJob.TranscriptionJobStatus;

      if (status === 'COMPLETED') {
        const response = await fetch(TranscriptionJob.Transcript.TranscriptFileUri);
        const data = await response.json();
        return data.results.transcripts[0].transcript;
      } else if (status === 'FAILED') {
        throw new Error('Transcription failed.');
      }
    }
  };

  const handleTranscribe = async () => {
    if (!file) return;
    setTranscript('');
    setLoading(true);

    try {
      const credentials = await getAmplifyCredentials();
      const s3Key = await uploadToS3(credentials);
      const { transcribeClient, jobName } = await startTranscription(credentials, s3Key);
      const result = await pollTranscript(transcribeClient, jobName);
      setTranscript(result);
    } catch (error) {
      console.error('Transcription error:', error);
      setTranscript('Error occurred during transcription.');
    } finally {
      setLoading(false);
    }
  };

  const handleCallBot = async () => {
    if (selectedPrompts.length === 0) return alert('Please select one or more prompts.');
    if (!transcript) return alert('Please transcribe the call first.');

    setLambdaLoading(true);
    setLambdaResponse('');

    try {
      const session = await fetchAuthSession();
      const credentials = session.credentials;

      const lambdaClient = new LambdaClient({
        region: REGION,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      const cleanedPrompts = selectedPrompts.map(p =>
        p.replace(/Here is the call transcript:[:]?/i, '').trim()
      );

      const combinedPromptText = `
The following transcription is provided:
${transcript}

Based on this transcription, answer the following prompts **together**:
${cleanedPrompts.join(' AND ')}
`;

      const response = await lambdaClient.send(
        new InvokeCommand({
          FunctionName: 'callTranscribeBot',
          InvocationType: 'RequestResponse',
          Payload: JSON.stringify({ inputText: combinedPromptText })
        })
      );

      const responsePayload = JSON.parse(
        new TextDecoder("utf-8").decode(response.Payload)
      );
      const parsedBody = JSON.parse(responsePayload.body);

      const output =
        parsedBody?.data?.raw?.output?.message?.content?.[0]?.text?.replace(/\n/g, '') ||
        parsedBody?.data?.output ||
        'No response from model.';

      setLambdaResponse(output);
    } catch (err) {
      console.error('Error calling Lambda:', err);
      setLambdaResponse('Error: ' + err.message);
    } finally {
      setLambdaLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white shadow rounded-md">
      <h2 className="text-xl font-semibold mb-4">Upload and Transcribe Audio</h2>

      <div className="mb-4">
        <label className="block mb-1 font-medium">Select Audio File</label>
        <input
          type="file"
          accept="audio/*"
          onChange={(e) => {
            const selectedFile = e.target.files?.[0];
            setFile(selectedFile);
            setAudioURL(selectedFile ? URL.createObjectURL(selectedFile) : '');
          }}
          className="border border-gray-300 rounded p-2 w-full"
        />
        {audioURL && (
          <div className="mt-2">
            <label className="block mb-1">Play Uploaded Audio</label>
            <audio controls src={audioURL} className="w-full" />
          </div>
        )}
      </div>

      <button
        onClick={handleTranscribe}
        disabled={!file || loading}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        {loading ? 'Transcribing...' : 'Start Transcription'}
      </button>

      {transcript && (
        <div className="mt-4">
          <label className="block font-medium mb-1">Transcript Result</label>
          <textarea
            readOnly
            rows={8}
            value={transcript}
            className="w-full border border-gray-300 rounded p-2"
          />
        </div>
      )}

      <hr className="my-6" />

      <h3 className="text-lg font-semibold mb-2">Select Prompt(s) and Call Bot</h3>
      <label className="block mb-1">Choose Prompt(s)</label>
      <select
        multiple
        className="w-full border border-gray-300 rounded p-2 mb-4"
        style={{ minHeight: '200px' }}
        value={selectedPrompts}
        onChange={(e) =>
          setSelectedPrompts(Array.from(e.target.selectedOptions, (opt) => opt.value))
        }
      >
        {promptMaster?.map((item, index) => (
          <option key={index} value={item.promptname}>
            {item.promptcategory} - {item.promptname}
          </option>
        ))}
      </select>

      <button
        onClick={handleCallBot}
        disabled={lambdaLoading}
        className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
      >
        {lambdaLoading ? 'Calling Bot...' : 'Call Bot'}
      </button>

      {lambdaResponse && (
        <div className="mt-4">
          <label className="block font-medium mb-1">Bot Response</label>
          <textarea
            readOnly
            rows={6}
            value={lambdaResponse}
            className="w-full border border-gray-300 rounded p-2"
          />
        </div>
      )}
    </div>
  );
};

export default TranscribeTab;

